import React, {
  useEffect,
  useState,
  createContext,
  useCallback,
  Suspense,
  lazy,
} from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import "./themes.css";
import Login from "./components/Login/Login";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import TopBar from "./components/TopBar/TopBar";
// SuperTopBar removed - index info now shown in OptionChainControls
import TradingControlsPanel from "./components/TradingControls/TradingControlsPanel";
import TokenRefreshBanner from "./components/TokenRefreshBanner/TokenRefreshBanner";
import { isAuthenticated } from "./services/auth";
import {
  fetchPositions,
  processPositionsData,
} from "./services/positionsService";
import {
  getGeneralData,
  getExpiriesData,
  sortIndicesByNearestExpiry,
} from "./services/generalDataCache";
import { usePositionsData, useIndicesData } from "./hooks/useMarketData";
import { ToastProvider, useToast } from "./components/Toast/ToastContext";
import { TradingProvider } from "./contexts/TradingContext";
import api from "./services/api";

// Error Boundaries
import {
  CompositeErrorBoundary,
  OptionChainErrorBoundary,
  PositionsErrorBoundary,
} from "./components/ErrorBoundaries";

// Suspense Loading Components
import {
  OptionChainLoader,
  PositionsLoader,
  LazyComponentLoader,
} from "./components/Suspense/LoadingBoundaries";

// Modern React hooks
import { useConcurrentUI, useRealTimeUpdates } from "./hooks/useModernReact";

// Direct imports for small components (no code splitting needed)
import FailedOrdersTable from "./components/Positions/FailedOrdersTable";
import CompactDerivedChart from "./components/DerivedData/CompactDerivedChart";
import PnLTimeChart from "./components/PnL/PnLTimeChart";
import MobileHomeView from "./components/MobileHome/MobileHomeView";
import useIsMobile from "./hooks/useIsMobile";

// Lazy load heavy components for better performance
const OptionChain = lazy(() => import("./components/OptionChain/OptionChain"));
const Positions = lazy(() => import("./components/Positions/Positions"));
const Orders = lazy(() => import("./components/Orders/Orders"));
const Portfolio = lazy(() => import("./components/Portfolio/Portfolio"));
const Strategies = lazy(() => import("./components/Strategies/Strategies"));
// Create Theme Context without toggle functionality
export const ThemeContext = createContext();

function AppContent() {
  // Always use light theme
  const [theme] = useState("light");
  const isMobile = useIsMobile();

  // Toast context for error boundaries
  const { showError, showSuccess, showWarning } = useToast();

  // Modern React concurrent features
  const { isPending } = useConcurrentUI();

  // Real-time updates management
  const {
    data: realtimeData,
    processPendingUpdates,
    hasPendingUpdates,
  } = useRealTimeUpdates({});

  // Set light theme on initial render
  useEffect(() => {
    document.body.setAttribute("data-theme", "light");
  }, []);
  const [indexes, setIndexes] = useState({});
  const [indexStreamData, setIndexStreamData] = useState({});
  const [sortedIndices, setSortedIndices] = useState([]); // Sorted indices from backend
  const [selectedIndex, setSelectedIndex] = useState("");
  const [selectedExpiry, setSelectedExpiry] = useState("");
  const [positions, setPositions] = useState({ open: [], closed: [] });
  const [defaultQuantity, setDefaultQuantity] = useState(35);

  // Debug wrapper for setDefaultQuantity
  const handleDefaultQuantityChange = useCallback((newQty) => {
    setDefaultQuantity(newQty);
  }, []);
  const [strikesToFetch] = useState(10);

  // Max lots data state
  const [maxLotsData, setMaxLotsData] = useState({
    available_margin: 0,
    max_lots: 0,
    margin_per_lot: 0,
    loading: false,
    error: null,
  });

  const [generalData, setGeneralData] = useState(null); // Cache for general data

  // Position filter removed
  const [positionsSummary, setPositionsSummary] = useState({
    total_call_value: 0,
    total_put_value: 0,
    total_value: 0,
    total_call_pending: 0,
    total_put_pending: 0,
    total_pending_value: 0,
  });

  const location = useLocation();
  const navigate = useNavigate();
  const layoutParam = new URLSearchParams(location.search).get("layout");
  const layoutDebugEnabled =
    (location.pathname === "/home" || location.pathname === "/option-chain") &&
    layoutParam === "debug";


  // Get active panel from URL path
  const getActivePanelFromPath = useCallback(() => {
    const path = location.pathname.substring(1); // Remove leading slash
    if (!path || path === "option-chain") return "home";
    return path;
  }, [location.pathname]);

  const handlePanelChange = useCallback(
    (panel) => {
      navigate(`/${panel}`);
    },
    [navigate],
  );

  // Memoized handlers for OptionChain
  const handleIndexChange = useCallback((index) => {
    setSelectedIndex(index);
  }, []);

  const handleExpiryChange = useCallback((expiry) => {
    setSelectedExpiry(expiry);
  }, []);

  // Fetch max lots from backend (manual trigger only via refresh button)
  const fetchMaxLots = useCallback(async () => {
    if (!selectedIndex || !selectedExpiry) {
      showError("Please select both index and expiry first");
      return;
    }

    if (!isAuthenticated()) {
      showError("Authentication required to fetch margin data");
      return;
    }

    setMaxLotsData((prev) => ({ ...prev, loading: true, error: null }));

    try {
      // Add timestamp to bypass browser cache (prevent 304 Not Modified)
      const timestamp = new Date().getTime();
      const response = await api.get(
        `/margin/max-lots?index=${selectedIndex}&expiry=${selectedExpiry}&_t=${timestamp}`,
      );

      if (response.data.success && response.data.data) {
        setMaxLotsData({
          ...response.data.data,
          loading: false,
          error: null,
        });
        showSuccess(
          `Max lots: ${response.data.data.max_lots} (Margin: ₹${response.data.data.margin_per_lot.toLocaleString("en-IN")}/lot)`,
        );
      } else {
        throw new Error(
          response.data.message || "Failed to calculate max lots",
        );
      }
    } catch (error) {
      console.error("Error fetching max lots:", error);
      setMaxLotsData((prev) => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
      showError("Failed to calculate max lots");
    }
  }, [selectedIndex, selectedExpiry, showError, showSuccess]);

  const refreshPositions = useCallback(async () => {
    try {
      const data = await fetchPositions();
      const processedData = processPositionsData(data);
      setPositions({
        open: processedData.open,
        closed: processedData.closed,
      });
      setPositionsSummary(processedData.summary);
      showSuccess("Positions refreshed successfully");
    } catch (error) {
      showError("Failed to refresh positions");
      throw error;
    }
  }, [showError, showSuccess]);

  const renderPositionsPanel = useCallback(
    (className = "positions-wrapper") => (
      <div className={className}>
        <PositionsErrorBoundary
          onRefreshPositions={refreshPositions}
          onFallbackPolling={() => {
            showWarning("Switched to periodic position updates");
          }}
          fallbackSummary={positionsSummary}
          showToast={showError}
        >
          <PositionsLoader>
            <Positions
              positions={positions}
              selectedIndex={selectedIndex}
              selectedExpiry={selectedExpiry}
              summary={positionsSummary}
              defaultQuantity={defaultQuantity}
              indexStreamData={indexStreamData}
            />
          </PositionsLoader>
        </PositionsErrorBoundary>

        <FailedOrdersTable />
        <CompactDerivedChart selectedIndex={selectedIndex} />
        <PnLTimeChart />
      </div>
    ),
    [
      defaultQuantity,
      indexStreamData,
      positions,
      positionsSummary,
      refreshPositions,
      selectedExpiry,
      selectedIndex,
      showError,
      showWarning,
    ],
  );

  // Load expiries data once on mount
  useEffect(() => {
    const loadExpiriesData = async () => {
      if (!isAuthenticated()) return;

      try {
        const expiriesData = await getExpiriesData();

        // Store the entire index->expiries mapping
        setIndexes(expiriesData);

        // Get sorted indices by nearest expiry. Prefer the concrete expiry map
        // so first paint and fallback behavior match the option-chain contract.
        const generalData = await getGeneralData();
        const backendSortedIndices = Array.isArray(generalData.sorted_indices)
          ? generalData.sorted_indices
          : [];
        const expirySortedIndices = sortIndicesByNearestExpiry(
          Object.keys(expiriesData),
          expiriesData,
        );
        const nextSortedIndices =
          expirySortedIndices.length > 0
            ? expirySortedIndices
            : backendSortedIndices;
        setSortedIndices(nextSortedIndices);

        // Only set initial values if no index is selected yet
        if (!selectedIndex) {
          if (nextSortedIndices.length > 0) {
            const firstIndex = nextSortedIndices[0];
            setSelectedIndex(firstIndex);

            // Check if we have expiries for this index and it's an array
            if (
              expiriesData[firstIndex] &&
              Array.isArray(expiriesData[firstIndex]) &&
              expiriesData[firstIndex].length > 0
            ) {
              setSelectedExpiry(expiriesData[firstIndex][0]);
            }
          }
        }
      } catch (err) {
        console.error("Error loading expiries data:", err);
      }
    };

    loadExpiriesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Re-run when route changes (e.g., /login -> /home)

  // Fetch lot sizes and default quantity from API when selected index changes
  // Load general data once on mount
  useEffect(() => {
    const loadGeneralData = async () => {
      if (!isAuthenticated()) return;

      try {
        const data = await getGeneralData();
        setGeneralData(data);

        // Set default quantity if index is already selected
        if (selectedIndex && data.indices[selectedIndex]?.lot_size) {
          setDefaultQuantity(data.indices[selectedIndex].lot_size);
        }
      } catch (error) {
        console.error("Error loading general data:", error);
      }
    };

    loadGeneralData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Re-run after login navigation so cookies are present

  // Update default quantity when selected index changes
  useEffect(() => {
    if (generalData?.indices && selectedIndex && generalData.indices[selectedIndex]?.lot_size) {
      setDefaultQuantity(generalData.indices[selectedIndex].lot_size);
    }
  }, [selectedIndex, generalData]);

  // WebSocket connections for real-time data (replaces SSE)
  const { data: positionsData } = usePositionsData();
  const { data: indicesData } = useIndicesData();

  // Process positions data when received via WebSocket
  // WebSocket now sends moves data with real-time LTPs (maxMoves=1 in backend)
  useEffect(() => {
    if (positionsData) {
      const processedData = processPositionsData(positionsData);
      setPositions({
        open: processedData.open,
        closed: processedData.closed,
      });
      setPositionsSummary(processedData.summary);
    }
  }, [positionsData]);

  // Process indices data when received via WebSocket
  useEffect(() => {
    if (indicesData && typeof indicesData === "object" && Object.keys(indicesData).length > 0) {
      setIndexStreamData((prevData) => {
        // Change detection - only update if data actually changed
        // This prevents unnecessary re-renders when WebSocket sends identical data
        if (JSON.stringify(prevData) === JSON.stringify(indicesData)) {
          return prevData; // No change, skip re-render
        }
        return indicesData;
      });
    }
  }, [indicesData]);

  // Initial fetch of positions on mount
  useEffect(() => {
    if (!isAuthenticated()) return;

    const fetchInitialPositions = async () => {
      try {
        const data = await fetchPositions();
        const processedData = processPositionsData(data);
        setPositions({
          open: processedData.open,
          closed: processedData.closed,
        });
        setPositionsSummary(processedData.summary);
      } catch (err) {
        console.error("Error fetching initial positions:", err);
      }
    };

    fetchInitialPositions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Re-run when route changes (e.g., /login -> /home)

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme: () => {} }}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <CompositeErrorBoundary
                level="critical"
                componentName="Trading Application"
                onEmergencyMode={() =>
                  showWarning(
                    "Emergency mode activated - limited functionality",
                  )
                }
              >
                <div
                  className={
                    layoutDebugEnabled
                      ? "app-container layout-debug"
                      : "app-container"
                  }
                >
                  <TokenRefreshBanner />
                  <TopBar
                    onPanelChange={handlePanelChange}
                    activePanel={getActivePanelFromPath()}
                    maxLotsData={maxLotsData}
                    onRefreshMaxLots={fetchMaxLots}
                    selectedIndex={selectedIndex}
                    selectedExpiry={selectedExpiry}
                  />
                  <div className="main-content">
                    <div className="panel-content">
                      <Routes>
                        <Route
                          path="/"
                          element={<Navigate to="/home" replace />}
                        />
                        <Route
                          path="/option-chain"
                          element={<Navigate to="/home" replace />}
                        />
                        <Route
                          path="/home"
                          element={
                            <TradingProvider
                              externalSelectedIndex={selectedIndex}
                              externalSelectedExpiry={selectedExpiry}
                              externalDefaultQuantity={defaultQuantity}
                              onIndexChange={handleIndexChange}
                              onExpiryChange={handleExpiryChange}
                              onDefaultQuantityChange={handleDefaultQuantityChange}
                            >
                              {isMobile ? (
                                <MobileHomeView
                                  positions={positions}
                                  summary={positionsSummary}
                                  selectedIndex={selectedIndex}
                                  selectedExpiry={selectedExpiry}
                                  defaultQuantity={defaultQuantity}
                                  indexStreamData={indexStreamData}
                                  onDefaultQuantityChange={handleDefaultQuantityChange}
                                  onRefreshPositions={refreshPositions}
                                />
                              ) : (
                                <>
                                  <TradingControlsPanel
                                    openPositions={positions.open}
                                    indexStreamData={indexStreamData}
                                    selectedIndex={selectedIndex}
                                  />

                                  <div className="main-trading-area">
                                    {renderPositionsPanel("positions-wrapper")}

                                    <div className="option-chain-wrapper">
                                      {/* Home market view with comprehensive error handling */}
                                      <OptionChainErrorBoundary
                                        selectedIndex={selectedIndex}
                                        selectedExpiry={selectedExpiry}
                                        onDataRefresh={async () => {
                                          showWarning(
                                            "Refreshing option chain data...",
                                          );
                                          // Trigger option chain refresh
                                        }}
                                        onFallbackMode={(cachedData) => {
                                          showWarning(
                                            "Using cached option chain data",
                                          );
                                        }}
                                      >
                                        <OptionChainLoader
                                          selectedIndex={selectedIndex}
                                          selectedExpiry={selectedExpiry}
                                        >
                                          <OptionChain
                                            strikesToFetch={strikesToFetch}
                                            indexes={indexes}
                                            indicesConfig={generalData?.indices}
                                            sortedIndices={sortedIndices}
                                            indexStreamData={indexStreamData}
                                            selectedIndex={selectedIndex}
                                          />
                                        </OptionChainLoader>
                                      </OptionChainErrorBoundary>
                                    </div>
                                  </div>
                                </>
                              )}
                            </TradingProvider>
                          }
                        />
                        <Route
                          path="/positions"
                          element={
                            <div className="positions-page">
                              {renderPositionsPanel(
                                "positions-wrapper positions-page__content",
                              )}
                            </div>
                          }
                        />
                        {/* Settings route with lazy loading */}
                        <Route
                          path="/settings"
                          element={
                            <LazyComponentLoader componentName="Settings">
                              <div className="settings-panel">
                                <h3>Settings</h3>
                                <p>Settings panel content goes here.</p>
                              </div>
                            </LazyComponentLoader>
                          }
                        />
                        {/* Reports route with lazy loading */}
                        <Route
                          path="/reports"
                          element={
                            <LazyComponentLoader componentName="Reports">
                              <div className="reports-panel">
                                <h3>Reports</h3>
                                <p>Reports panel content goes here.</p>
                              </div>
                            </LazyComponentLoader>
                          }
                        />
                        <Route
                          path="/orders"
                          element={
                            <Suspense
                              fallback={
                                <div className="loading">Loading Orders...</div>
                              }
                            >
                              <Orders />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/portfolio"
                          element={
                            <Suspense
                              fallback={
                                <div className="loading">Loading Portfolio...</div>
                              }
                            >
                              <Portfolio />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/strategies"
                          element={
                            <Suspense
                              fallback={
                                <div className="loading">Loading Strategies...</div>
                              }
                            >
                              <Strategies />
                            </Suspense>
                          }
                        />
                      </Routes>
                    </div>
                  </div>

                  {/* Pending updates indicator */}
                  {isPending && (
                    <div className="position-fixed bottom-0 end-0 p-3">
                      <div className="toast show" role="alert">
                        <div className="toast-body d-flex align-items-center">
                          <div
                            className="spinner-border spinner-border-sm text-primary me-2"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Updating data...
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Process pending real-time updates */}
                  {hasPendingUpdates && (
                    <div className="position-fixed bottom-0 start-0 p-3">
                      <button
                        className="btn btn-sm btn-outline-info"
                        onClick={processPendingUpdates}
                      >
                        <i className="fas fa-sync me-1"></i>
                        Apply Updates ({Object.keys(realtimeData).length})
                      </button>
                    </div>
                  )}
                </div>
              </CompositeErrorBoundary>
            </ProtectedRoute>
          }
        />
      </Routes>
    </ThemeContext.Provider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
